import { useEffect, useState, type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import { useSession } from '../auth/useSession';
import { displayName } from '../auth/sessionStore';
import { roleLabel } from './roleLabels';
import { IconAnalytics, IconClients, IconContractWork, IconFinance, IconMaterials, IconMessages, IconOrders, IconPlanning, IconProduction, IconSearch, IconSuppliers, IconProcurement } from './icons';
import { hasUnseenClient, sectionOf, useBadges } from './badges';

export interface SidebarItem { label: string; to: string; icon?: ComponentType<{ size?: number }>; end?: boolean }
export interface SidebarGroup { title: string; items: SidebarItem[] }

/** Навигация erp_v3: одна роль, гейтов по правам нет. Группы РАБОТА · СПРАВОЧНИКИ · ДЕНЬГИ. */
export const SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    title: 'Работа',
    items: [
      { label: 'Сегодня', to: '/', icon: IconProduction, end: true },
      { label: 'Заявки', to: '/inquiries', icon: IconClients },
      { label: 'Заказы', to: '/orders', icon: IconOrders },
      { label: 'Закупки', to: '/purchases', icon: IconProcurement },
      { label: 'Сообщения', to: '/messages', icon: IconMessages },
      { label: 'Встречи', to: '/meetings', icon: IconPlanning },
      { label: 'ОТК', to: '/qc', icon: IconContractWork },
      { label: 'Склад', to: '/stock', icon: IconMaterials },
    ],
  },
  {
    title: 'Справочники',
    items: [
      { label: 'Контрагенты', to: '/counterparties', icon: IconClients },
      { label: 'Изделия', to: '/products', icon: IconSuppliers },
      { label: 'Материалы', to: '/materials', icon: IconMaterials },
      { label: 'Настройки', to: '/settings', icon: IconContractWork },
    ],
  },
  {
    title: 'Деньги',
    items: [
      { label: 'Финансы', to: '/finance', icon: IconFinance },
      { label: 'Аналитика', to: '/analytics', icon: IconAnalytics },
    ],
  },
];

const isMac = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform ?? '');

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const [a = '', b = ''] = parts;
  if (!a) return '·';
  if (!b) return a.slice(0, 2).toUpperCase();
  return (a.charAt(0) + b.charAt(0)).toUpperCase();
}

const plural = (n: number, forms: [string, string, string]): string => { const m10 = n % 10; const m100 = n % 100; return m10 === 1 && m100 !== 11 ? forms[0] : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? forms[1] : forms[2]; };

const SIDE_KEY = 'wg.side';
/** Меню свёрнуто до значков (новый визуал, шаг 1): признак на <html data-side="rail">, выбор помнится в этом браузере. */
function useRail(): [boolean, () => void] {
  const [rail, setRail] = useState<boolean>(() => { try { return localStorage.getItem(SIDE_KEY) === 'rail'; } catch { return false; } });
  useEffect(() => {
    if (rail) document.documentElement.setAttribute('data-side', 'rail'); else document.documentElement.removeAttribute('data-side');
    try { localStorage.setItem(SIDE_KEY, rail ? 'rail' : 'full'); } catch { /* приватный режим */ }
  }, [rail]);
  return [rail, () => setRail((r) => !r)];
}

export function GlobalSidebar({ onOpenSearch }: { onOpenSearch: () => void }) {
  const { user } = useSession();
  const [rail, toggleRail] = useRail();
  const name = displayName(user);
  const role = roleLabel(user?.roles ?? []);
  // Счётчики дел по разделам (этап 15; смысл — этап 17): число — дела, ждущие действия менеджера; точка — в разделе есть дело от клиента, которое ещё не открывали.
  const badges = useBadges();
  return (
    <nav className="sidebar" aria-label="Основная навигация">
      <div className="sidebar__brand">
        <div className="sidebar__brand-name">
          <span className="sidebar__brand-full">WearGroup</span>
          <span className="sidebar__brand-short" aria-hidden="true">WG</span>
        </div>
        <div className="sidebar__brand-sub">Производственная система</div>
        <button type="button" className="sidebar__toggle" onClick={toggleRail} title={rail ? 'Развернуть меню' : 'Свернуть меню до значков'} aria-label={rail ? 'Развернуть меню' : 'Свернуть меню до значков'} aria-pressed={rail}>
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d={rail ? 'M6 3l5 5-5 5' : 'M10 3L5 8l5 5'} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <button type="button" className="sidebar__search" onClick={onOpenSearch} title="Поиск">
        <IconSearch />
        <span className="sidebar__search-label">Поиск</span>
        <kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>
      <div className="sidebar__nav">
        {SIDEBAR_GROUPS.map((group) => (
          <div className="nav-group" key={group.title}>
            <div className="nav-group__title">{group.title}</div>
            {group.items.map((item) => {
              const Icon = item.icon;
              const section = sectionOf(item.to);
              const count = section ? (badges.counts[section] ?? 0) : 0;
              const fresh = !!section && hasUnseenClient(section, badges);
              const hint = count > 0 ? `${item.label}: ${count} ${plural(count, ['дело', 'дела', 'дел'])}${fresh ? ', есть новое' : ''}` : item.label;
              return (
                <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}${fresh ? ' has-new' : ''}`} title={hint}>
                  {Icon ? <Icon size={14} /> : null}
                  <span className="nav-item__label">{item.label}</span>
                  {count > 0 && <span className="nav-item__badge" aria-label={`${count} ${plural(count, ['дело', 'дела', 'дел'])}`}>{count > 99 ? '99+' : count}</span>}
                  {fresh && <span className="nav-item__dot" aria-label="Есть новое" />}
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>
      {user && (
        <NavLink to="/me" className={({ isActive }) => `sidebar__user${isActive ? ' is-active' : ''}`} title={`${name} · ${role} — личный кабинет`}>
          <div className="sidebar__avatar" aria-hidden="true">{initials(name)}</div>
          <div style={{ minWidth: 0 }}>
            <div className="sidebar__user-name">{name}</div>
            <div className="sidebar__user-role">{role}</div>
          </div>
        </NavLink>
      )}
    </nav>
  );
}
